import type { Agent } from "@/services/assistant.query";
import { type AgentTool, getTool } from "@/services/tool.service";
import { ApiError } from "@/lib/api-client";
import { exportedTool } from "@/utils/tool-options";
import { GEMINI_3_8_LIVE, agentModelOf, isTtsModel } from "@/constants/liveModels";
import { isKnownTranscriber } from "@/constants/transcribers";
import { isFeatureOn } from "@/services/feature-flags.service";

export const downloadAgentConfig = async (agent: Agent) => {
  if (!agent) return;

  // Fetch full tool definitions for this agent's toolIds, in their order. An id
  // whose tool is gone (404), or that is no id at all (422 — some agents carry
  // template placeholders), is skipped; any other failure fails the export
  // rather than shipping a file with tools silently missing.
  const toolRows = await Promise.all(
    (agent.config?.toolIds ?? []).map((id) =>
      getTool(id).catch((error: unknown): null => {
        if (error instanceof ApiError && (error.status === 404 || error.status === 422)) return null;
        throw error;
      }),
    ),
  );

  // Remove IDs and agent-specific fields - keep only the tool configuration
  const tools = toolRows.filter((tool): tool is AgentTool => tool !== null).map(exportedTool);

  // Create export with cleared IDs and embedded tools. Knowledge-base links point at
  // this workspace's stores, so no `…StoreIds` list travels (an agent last saved before
  // 2026-10-21 can still carry the old fileSearchStoreIds).
  const exportConfig: Agent["config"] = {
    ...(Object.fromEntries(
      Object.entries(agent.config ?? {}).filter(([key]) => !key.endsWith("StoreIds")),
    ) as Agent["config"]),
    toolIds: [],            // Clear - will be repopulated on import
    kbStoreIds: [],
  };

  const exportData = {
    agent_name: agent.agent_name,
    agent_type: agent.agent_type,
    config: exportConfig,
    tools: tools,           // Full tool definitions
    exported_at: new Date().toISOString(),
    version: "2.0"          // New version to indicate enhanced format
  };

  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${agent.agent_name.replace(/\s+/g, '_')}_config.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

type Dict = Record<string, unknown>;

/** A file's voice, carrying its voice model only when it is one an agent can speak with (else the default applies). */
const importedVoice = ({ ttsModel, ...voice }: Dict) => (isTtsModel(ttsModel) ? { ...voice, ttsModel } : voice);

/**
 * The config an imported agent is created with. Store links point at one
 * workspace's stores, so none travel with an export: every `…StoreIds` list is
 * dropped — including one an older export file carries under a store key that
 * has since been renamed — and the current keys start empty.
 *
 * The file's model and voice model come along when they are ones an agent can
 * run on (shared/agent-models.json): any other model is Gemini 3.8 Live, and any
 * other voice model is left out, as is a transcriber the server would refuse. A text model comes along only while
 * shared/feature-flags.json offers the traditional engine; held back, the agent
 * runs on Gemini 3.8 Live. The rest of the file's `llm` and `voice` is kept.
 */
export const importedAgentConfig = (config: Dict) => {
  const llm = config.llm as Dict | undefined;
  const voice = config.voice;
  return {
    ...Object.fromEntries(
      Object.entries(config).filter(
        ([key, value]) => !key.endsWith("StoreIds") && (key !== "transcriber" || isKnownTranscriber(value)),
      ),
    ),
    llm: { ...llm, model: isFeatureOn("traditionalEngine") ? agentModelOf(llm?.model) : GEMINI_3_8_LIVE },
    ...(voice && typeof voice === "object" ? { voice: importedVoice(voice as Dict) } : {}),
    toolIds: [], // Will be populated after tool creation
    kbStoreIds: [],
  };
};
